import { create } from 'zustand';

import { registerReloadBlocker } from '#features/error/utils/reloadGuards';
import type { MobileAnchor, WindowCorner, WindowMode } from '#features/media-window/model';

/**
 * `elsewhere` — звонок идёт, но на другом устройстве этого же человека: подключаться к комнате
 * нельзя (второй клиент выбил бы первого), поэтому показываем экран с кнопкой «перенести сюда».
 */
export type CallPhase = 'idle' | 'outgoing' | 'incoming' | 'active' | 'ended' | 'elsewhere';

export type CallPeer = {
  userId: string;
  displayName: string | null;
  avatar: string | null;
};

export type CallInfo = {
  callId: string;
  chatId: string;
  roomName: string;
  peer: CallPeer;
  /** true when the current user is the caller. */
  startedByMe: boolean;
  /** false — audio call (camera starts off); true — video call. */
  withVideo: boolean;
  /** LiveKit join credentials — present once granted by start/answer/active. */
  livekitUrl: string | null;
  token: string | null;
  /** Local clock ms when the call went active (drives the duration timer). */
  acceptedAt: number | null;
  /**
   * Local clock ms when the phone started ringing — drives the countdown on the ring
   * screen. Server time (`createdAt`) where we have it, so a reload mid-ring shows the
   * time actually left rather than a fresh 45s; local `Date.now()` on the optimistic
   * outgoing screen and on the incoming WS frame, which arrive as the ring starts.
   */
  ringingSince: number | null;
};

/**
 * How long the callee is rung before the server marks the call MISSED.
 * Mirrors `app.call.ring-timeout` (CallProperties.ringTimeout, teamplay-comment).
 */
export const RING_TIMEOUT_MS = 45_000;

/** Desktop floating-window corner. Shared with session voice — same window, same corners. */
export type CallCorner = WindowCorner;

/** Край экрана на телефоне. Тоже общий с голосом сессии — жест и смысл у них один. */
export type CallAnchor = MobileAnchor;

/** How the in-call widget is shown: mini pill, floating window or fullscreen. */
export type CallWindowMode = WindowMode;

type CallSlice = {
  phase: CallPhase;
  call: CallInfo | null;
  /** Why the call ended — shown briefly in the 'ended' phase. */
  endReason: string | null;
  micEnabled: boolean;
  camEnabled: boolean;
  /** mini pill ↔ floating window ↔ fullscreen. */
  windowMode: CallWindowMode;
  /** Desktop floating-window corner. */
  corner: CallCorner;
  /**
   * Which edge the mini pill sits at on a phone. At the bottom it lands on the message composer
   * in a chat thread — the one place people need both the call and the keyboard at once.
   */
  mobileAnchor: CallAnchor;
  setOutgoing: (call: CallInfo) => void;
  /** Ignored unless idle/ended — one ring UI at a time. */
  setIncoming: (call: CallInfo) => void;
  setActive: (credentials?: { livekitUrl?: string | null; token?: string | null }) => void;
  /** Звонок жив, но им владеет другой наш клиент. */
  setElsewhere: (call: CallInfo) => void;
  setEnded: (reason: string) => void;
  setMicEnabled: (enabled: boolean) => void;
  setCamEnabled: (enabled: boolean) => void;
  setWindowMode: (mode: CallWindowMode) => void;
  setCorner: (corner: CallCorner) => void;
  setMobileAnchor: (anchor: CallAnchor) => void;
  reset: () => void;
};

export const useCallStore = create<CallSlice>()((set, get) => ({
  phase: 'idle',
  call: null,
  endReason: null,
  micEnabled: true,
  camEnabled: true,
  windowMode: 'window',
  corner: 'br',
  mobileAnchor: 'bottom',
  setOutgoing: (call) => {
    set({
      phase: 'outgoing',
      call,
      endReason: null,
      micEnabled: true,
      camEnabled: call.withVideo,
      windowMode: 'window',
    });
  },
  setIncoming: (call) => {
    const { phase } = get();
    // Из `elsewhere` пускаем: если пришёл новый входящий, прежний звонок на другом устройстве уже
    // не наша забота — экран переноса ему больше не нужен.
    if (phase !== 'idle' && phase !== 'ended' && phase !== 'elsewhere') {
      return;
    }
    set({
      phase: 'incoming',
      call,
      endReason: null,
      micEnabled: true,
      camEnabled: call.withVideo,
      windowMode: 'window',
    });
  },
  setActive: (credentials) => {
    const { call } = get();
    if (!call) {
      return;
    }
    set({
      phase: 'active',
      endReason: null,
      call: {
        ...call,
        livekitUrl: credentials?.livekitUrl ?? call.livekitUrl,
        token: credentials?.token ?? call.token,
        acceptedAt: call.acceptedAt ?? Date.now(),
      },
    });
  },
  setElsewhere: (call) => {
    set({
      phase: 'elsewhere',
      call,
      endReason: null,
      windowMode: 'window',
    });
  },
  setEnded: (reason) => {
    if (get().phase === 'idle') {
      return;
    }
    set({ phase: 'ended', endReason: reason });
  },
  setMicEnabled: (enabled) => set({ micEnabled: enabled }),
  setCamEnabled: (enabled) => set({ camEnabled: enabled }),
  setWindowMode: (windowMode) => set({ windowMode }),
  setCorner: (corner) => set({ corner }),
  // Kept across calls for the same reason as the corner: the person moved it out of their way once.
  setMobileAnchor: (mobileAnchor) => set({ mobileAnchor }),
  reset: () =>
    set({
      phase: 'idle',
      call: null,
      endReason: null,
      micEnabled: true,
      camEnabled: true,
      windowMode: 'window',
    }),
}));

// Пока идёт звонок, автоперезагрузка из-за протухшего чанка запрещена: она молча оборвала бы
// разговор ради того, чтобы человек увидел свежую сборку. Честный экран ошибки — меньшее зло,
// а перезагрузиться руками он сможет, когда положит трубку.
//
// Регистрация на уровне модуля, а не в компоненте: звонок невозможен, если этот модуль не
// загружен, — значит и подписываться раньше незачем.
registerReloadBlocker(() => {
  const { phase } = useCallStore.getState();

  return phase === 'outgoing' || phase === 'incoming' || phase === 'active';
});
